/**
 * Quiz Game Types
 * Type definitions for the quiz game module
 */

/**
 * Quiz entity (from quizzes table)
 */
export interface Quiz {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  color: string;
  weekend_id: string | null;
  is_active: boolean;
  is_weekend_enabled: boolean;
  question_count: number;
  time_per_question: number;
  randomize_questions: boolean;
  points_per_correct: number;
  bonus_speed: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Custom timing
  use_custom_dates: boolean;
  starts_at: string | null;
  ends_at: string | null;
  // Play restrictions
  is_team_based: boolean;
  allow_unlimited_plays: boolean;
  max_plays_per_user: number;
  max_plays_per_team: number;
  allow_retry_on_zero: boolean;
  // Computed (from join)
  questions_created?: number;
}

/**
 * Quiz play status for a user/team
 */
export interface QuizPlayStatus {
  can_play: boolean;
  reason?: "max_plays_reached" | "team_member_played" | "quiz_ended" | "quiz_not_started";
  plays_count: number;
  max_plays: number;
  played_by?: {
    user_id: string;
    display_name: string;
    score: number;
  }[];
}

/**
 * Quiz Category
 */
export interface QuizCategory {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  color: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * Question types
 */
export type QuestionType = "multiple_choice" | "true_false" | "text_answer";

/**
 * Question difficulty levels
 */
export type QuestionDifficulty = "easy" | "medium" | "hard";

/**
 * Multiple choice option
 */
export interface QuizOption {
  text: string;
  is_correct: boolean;
}

/**
 * Quiz Question
 */
export interface QuizQuestion {
  id: string;
  quiz_id: string | null;
  section_id: string | null;
  category_id: string | null;
  category?: QuizCategory;
  question: string;
  question_type: QuestionType;
  options: QuizOption[];
  correct_answer: string | null;
  explanation: string | null;
  difficulty: QuestionDifficulty;
  image_url: string | null;
  points: number;
  bonus_points_fast: number;
  time_limit_seconds: number;
  order_index: number;
  is_family_specific: boolean;
  created_by: string | null;
  is_active: boolean;
  times_answered: number;
  times_correct: number;
  created_at: string;
  updated_at: string;
}

/**
 * Quiz Answer (user's answer to a question)
 */
export interface QuizAnswer {
  id: string;
  session_id: string;
  question_id: string;
  user_id: string;
  selected_option_index: number | null;
  text_answer: string | null;
  is_correct: boolean;
  time_taken_ms: number | null;
  points_earned: number;
  answered_at: string;
  // Joined
  question?: QuizQuestion;
}

/**
 * Submit answer result from RPC
 */
export interface SubmitAnswerResult {
  answer_id: string;
  is_correct: boolean;
  points_earned: number;
  correct_option_index: number | null;
  explanation: string | null;
  error?: string;
}

/**
 * Quiz game state (stored in game_sessions.game_state)
 */
export interface QuizGameState {
  /** Questions for this session (just IDs) */
  question_ids: string[];
  /** Current question index */
  current_index: number;
  /** Total questions */
  total_questions: number;
  /** Category filter (if any) */
  category_id: string | null;
  /** Difficulty filter (if any) */
  difficulty: QuestionDifficulty | null;
  /** Time per question in seconds */
  time_per_question: number;
  /** Total score so far */
  total_score: number;
  /** Correct answers count */
  correct_count: number;
  /** Questions answered */
  answers: {
    question_id: string;
    is_correct: boolean;
    points_earned: number;
    time_taken_ms: number;
  }[];
}

/**
 * Quiz session result
 */
export interface QuizSessionResult {
  total_questions: number;
  correct_answers: number;
  wrong_answers: number;
  total_points: number;
  accuracy_percentage: number;
  average_time_ms: number;
  fastest_answer_ms: number;
  best_streak: number;
}

/**
 * Quiz configuration (from weekend_game_config.custom_config or game_definitions.default_config)
 */
export interface QuizConfig {
  /** Number of questions per session */
  questions_per_session: number;
  /** Default time limit per question (seconds) */
  default_time_limit: number;
  /** Points for correct answer */
  points_correct: number;
  /** Bonus points for fast answer */
  points_bonus_speed: number;
  /** Allow category selection */
  allow_category_selection: boolean;
  /** Allow difficulty selection */
  allow_difficulty_selection: boolean;
  /** Available categories (null = all) */
  available_categories: string[] | null;
  /** Randomize question order */
  randomize_questions: boolean;
  /** Show explanation after each answer */
  show_explanations: boolean;
  /** Allow retries (free play only) */
  allow_retries: boolean;
  /** Multiplayer settings */
  multiplayer?: {
    enabled: boolean;
    max_players: number;
    real_time: boolean;
  };
}

/**
 * Default quiz configuration
 */
export const DEFAULT_QUIZ_CONFIG: QuizConfig = {
  questions_per_session: 10,
  default_time_limit: 30,
  points_correct: 10,
  points_bonus_speed: 5,
  allow_category_selection: true,
  allow_difficulty_selection: true,
  available_categories: null,
  randomize_questions: true,
  show_explanations: true,
  allow_retries: true,
  multiplayer: {
    enabled: false,
    max_players: 4,
    real_time: false,
  },
};

/**
 * Quiz session setup options
 */
export interface QuizSessionSetup {
  quiz_id?: string | null;
  category_id?: string | null;
  difficulty?: QuestionDifficulty | null;
  question_count?: number;
  time_per_question?: number;
}

/**
 * Points breakdown for quiz
 */
export interface QuizPointsConfig {
  correct_answer: number;
  bonus_speed: number;
  participation: number;
  perfect_score_bonus: number;
}

export const DEFAULT_QUIZ_POINTS: QuizPointsConfig = {
  correct_answer: 10,
  bonus_speed: 5,
  participation: 5,
  perfect_score_bonus: 50,
};

